import React, { useState, useContext } from 'react';
import axios from 'axios';
import {
  Alert,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Stack,
  TextField,
  InputAdornment,
  Snackbar,
} from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import SensorsIcon from '@mui/icons-material/Sensors';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
// Leaflet Map Imports
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { Icon } from 'leaflet';
import { LanguageContext } from '../../../context/languageContext';
import WarningIcon from '@mui/icons-material/Warning';
import { validateAndSanitizeHostname } from './utilFunctions';
import { useAuth } from '../../../context/authContext';

const HughesSearch = () => {
  const { user } = useAuth();
  const { translations } = useContext(LanguageContext);
  const [serial, setSerial] = useState('');
  const [device, setDevice] = useState(null);
  const [cellularCheck, setCellularCheck] = useState(null);
  const [cellularCheckPerformed, setCellularCheckPerformed] = useState(false);
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [state, setState] = useState('');
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [signalStrength, setSignalStrength] = useState('');
  const [rebootLoading, setRebootLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  // regex to capture all numbers before the parenthesis of signal_strength
  const match = signalStrength.match(/^\d+/);
  const signalStrengthClean = match ? match[0] : 'No match found';

  // Snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsSnackbarOpen(false);
  };

  // map marker
  const customIcon = new Icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/128/684/684908.png',
    iconSize: [38, 38],
  });

  // Searching for Device Information
  const handleSearch = async () => {
    if (!serial) {
      setSnackbarMessage('Serial Number is required for search');
      setSnackbarSeverity('info');
      setIsSnackbarOpen(true);
      return;
    }

    setSearchLoading(true);
    const searchData = {
      serialNumber: `${serial}`,
      device: 'Hughes',
      userId: `${user.userId}`,
    };

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_ENDPOINT_AGENTUTILS}/comm/hughes`,
        searchData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      setDevice(res.data.data[0]);
      setState(res.data.data[0].state);
      setLat(res.data.data[0].latitudeActual);
      setLng(res.data.data[0].longitudeActual);
      setSnackbarMessage('Device Search Successful');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setSnackbarMessage(
          'Device Not Found, Please Check Serial Number and Device Type (Ventus or Hughes)'
        );
        setSnackbarSeverity('warning');
        setIsSnackbarOpen(true);
      } else {
        console.error('Error fetching device data:', error);
        setSnackbarMessage(
          'Error Fetching Device Data, Please Try Again. If Problem Persists, Contact Support.'
        );
        setSnackbarSeverity('error');
        setIsSnackbarOpen(true);
      }
      setDevice(null);
    } finally {
      // Set a timeout to re-enable the search button after 5 seconds
      setTimeout(() => {
        setSearchLoading(false);
      }, 5000);
    }
  };

  // Clearing Search Results
  const handleClear = () => {
    setSerial('');
    setDevice(null);
    setCellularCheck(null);
    setCellularCheckPerformed(false);
    setLat('');
    setLng('');
    setSnackbarMessage('Device Search Cleared');
    setSnackbarSeverity('info');
    setIsSnackbarOpen(true);
  };

  // Health Check
  const handleCellularCheck = async () => {
    try {
      const sanitizedHostname = validateAndSanitizeHostname(device.name);
      const res = await axios.post(
        `${process.env.REACT_APP_ENDPOINT_AGENTUTILS}/comm/hughes/ping`,
        { hostName: sanitizedHostname },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      setCellularCheck(res.data[0].results);
      setSignalStrength(res.data[0].results.signal_strength);
      setCellularCheckPerformed(true);
      setSnackbarMessage('Health Check Successful');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
    } catch (error) {
      console.error('Error Health Check on device:', error);
      setSnackbarMessage('Error Performing Health Check on Device');
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setCellularCheck(null);
      setCellularCheckPerformed(false);
    }
  };

  // Pinging Device.
  const handlePing = async () => {
    try {
      const sanitizedHostname = validateAndSanitizeHostname(device.name);
      await axios.post(
        `${process.env.REACT_APP_ENDPOINT_AGENTUTILS}/comm/hughes/ping`,
        { hostName: sanitizedHostname },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      setSnackbarMessage('Ping Successful');
      setSnackbarSeverity('info');
      setIsSnackbarOpen(true);
    } catch (error) {
      console.error('Error pinging device:', error);
      setSnackbarMessage('Ping Unsuccessful');
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
    }
  };

  // Reboot Device
  const handleReboot = () => {
    setDialogOpen(true);
  };

  const handleConfirmReboot = async () => {
    try {
      setRebootLoading(true); // start spinner

      setDialogOpen(false);
      setSnackbarMessage('Rebooting Device. Please wait 3 minutes.');
      setSnackbarSeverity('info');
      setIsSnackbarOpen(true);

      let sanitizedHostnameForReboot;
      try {
        sanitizedHostnameForReboot = validateAndSanitizeHostname(
          device.name
        ).toUpperCase();
      } catch (err) {
        console.error(`Invalid hostname: ${err.message}`);
        setRebootLoading(false);
        return;
      }

      const rebootData = {
        retailerNumber: device.customerLocationId,
        hostname: sanitizedHostnameForReboot,
        serialNumber: `${device.diuSerialNum}`,
        softwareVersion: `${device.softwareVersion}`,
        jurisdiction: device.stateUSA,
        device: 'Hughes',
        rebooted: 'YES', // or false based on your logic
        userId: `${user.userId}`,
      };

      await axios.post(
        `${process.env.REACT_APP_ENDPOINT_AGENTUTILS}/comm/hughes/reboot/confirmed`,
        rebootData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      // Spinner for 3 minutes then stop
      setTimeout(() => {
        setRebootLoading(false);
      }, 180000);
    } catch (error) {
      console.error('Error submitting reboot data:', error);
      setRebootLoading(false); // stop spinner if error
    }
  };

  const handleCancelReboot = async () => {
    try {
      let sanitizedHostnameForCancel;
      try {
        sanitizedHostnameForCancel = validateAndSanitizeHostname(
          device.name
        ).toUpperCase();
      } catch (err) {
        console.error(`Invalid hostname: ${err.message}`);
        return;
      }

      const rebootData = {
        retailerNumber: device.customerLocationId,
        hostname: sanitizedHostnameForCancel,
        serialNumber: `${device.diuSerialNum}`,
        softwareVersion: `${device.softwareVersion}`,
        jurisdiction: device.stateUSA,
        device: 'Hughes',
        rebooted: 'NO', // or false based on your logic
        userId: `${user.userId}`,
      };

      await axios.post(
        `${process.env.REACT_APP_ENDPOINT_AGENTUTILS}/comm/hughes/reboot/cancelled`,
        // `http://localhost:8080/v1/engage/contactcenter/comm/reboot`,
        rebootData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      setDialogOpen(false);
      setSnackbarMessage('Device Reboot Rejected');
      setSnackbarSeverity('info');
      setIsSnackbarOpen(true);
    } catch (error) {
      console.error('Error submitting reboot data:', error);
      setSnackbarMessage('Error Rejecting Device Reboot');
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
    }
  };

  return (
    <>
      <div className='container' style={{ display: 'flex' }}>
        <div style={{ flex: 1, padding: '10px' }}>
          <Stack
            direction='row'
            spacing={2}
            sx={{
              marginBottom: '20px',
            }}
          >
            <Button
              onClick={handleSearch}
              variant='outlined'
              color='primary'
              disabled={rebootLoading || searchLoading}
              sx={{ fontFamily: 'BrightstarSans-Regular' }}
            >
              {searchLoading ? 'Searching...' : translations.search || 'Search'}
            </Button>
            <Button
              onClick={handleClear}
              variant='outlined'
              color='warning'
              disabled={rebootLoading}
              sx={{ fontFamily: 'BrightstarSans-Regular' }}
            >
              {translations.clear || 'Clear'}
            </Button>
          </Stack>
          <TextField
            type='text'
            value={serial}
            onChange={e => setSerial(e.target.value)}
            placeholder={translations.searchSerialNumber || 'Search'}
            variant='outlined'
            fullWidth
            sx={{
              fontFamily: 'BrightstarSans-Regular',
              backgroundColor: '#ffffff',
              marginBottom: '50px',
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton onClick={handleSearch} aria-label='search'>
                    <SearchIcon
                      sx={{
                        color: '#053469',
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            label={translations.serialNumber || 'Serial Number'}
          />
          <div>
            {device && (
              <Card
                variant='outlined'
                style={{ display: 'flex', width: '100%' }}
              >
                <CardContent
                  style={{ flex: 1, display: 'flex', position: 'relative' }}
                >
                  <div style={{ flex: 1, position: 'relative' }}>
                    {cellularCheckPerformed && (
                      <>
                        <Typography
                          sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                          color='text.secondary'
                        >
                          {translations.cellularCheck || 'Cellular Check'}:{' '}
                          {signalStrengthClean > 105 ? (
                            <span
                              style={{
                                color: 'red',
                                fontFamily: 'BrightstarSans-Bold',
                              }}
                            >
                              {translations.bad || 'BAD'}
                            </span>
                          ) : signalStrengthClean < 105 &&
                            signalStrengthClean > 95 ? (
                            <span
                              style={{
                                color: 'orange',
                                fontFamily: 'BrightstarSans-Bold',
                              }}
                            >
                              {translations.fair || 'FAIR'}
                            </span>
                          ) : signalStrengthClean <= 95 ? (
                            <span
                              style={{
                                color: 'green',
                                fontFamily: 'BrightstarSans-Bold',
                              }}
                            >
                              {translations.good || 'GOOD'}
                            </span>
                          ) : null}
                        </Typography>
                      </>
                    )}
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      {translations.hostname || 'Hostname'}:{' '}
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {device.name.toUpperCase()}
                      </span>
                    </Typography>
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      {translations.serialNumber}:{' '}
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {device.diuSerialNum}
                      </span>
                    </Typography>
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      {translations.model || 'Model'}:{' '}
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {device.model}
                      </span>
                    </Typography>
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      {translations.deviceType}:{' '}
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {device.productGroup}
                      </span>
                    </Typography>
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      {translations.softwareVersion}:{' '}
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {device.softwareVersion}
                      </span>
                    </Typography>
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      {translations.location}:
                    </Typography>
                    {/* <Typography sx={{ mb: 1.5 }} color='text.secondary'>
                      <span style={{ color: '#154db4' }}>Site Name</span>
                    </Typography> */}
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {device.address}
                      </span>
                    </Typography>
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {device.city}, {device.stateUSA} {device.zip}
                      </span>
                    </Typography>
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      {translations.coordinates || 'Coordinates'}:{' '}
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {lng}, {lat}
                      </span>
                    </Typography>
                    {cellularCheck && (
                      <>
                        <hr />
                        <p style={{ fontFamily: 'BrightstarSans-Regular' }}>
                          Cellular Health Check Results:
                        </p>
                        <Typography
                          sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                          color='text.secondary'
                        >
                          {translations.status || 'Status'}:{' '}
                          {state === 5 ? (
                            <span
                              style={{
                                color: 'green',
                                fontFamily: 'BrightstarSans-Bold',
                              }}
                            >
                              {'Normal'}
                            </span>
                          ) : state === 4 ? (
                            <span
                              style={{
                                color: 'gray',
                                fontFamily: 'BrightstarSans-Bold',
                              }}
                            >
                              {'Unknown'}
                            </span>
                          ) : state === 3 ? (
                            <span
                              style={{
                                color: 'yellow',
                                fontFamily: 'BrightstarSans-Bold',
                              }}
                            >
                              {'Minor'}
                            </span>
                          ) : state === 2 ? (
                            <span
                              style={{
                                color: 'orange',
                                fontFamily: 'BrightstarSans-Bold',
                              }}
                            >
                              {'Major'}
                            </span>
                          ) : (
                            <span
                              style={{
                                color: 'red',
                                fontFamily: 'BrightstarSans-Bold',
                              }}
                            >
                              Critical
                            </span>
                          )}
                        </Typography>
                        <Typography
                          sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                          color='text.secondary'
                        >
                          Uptime:{' '}
                          <span
                            style={{
                              color: '#154db4',
                              fontFamily: 'BrightstarSans-Regular',
                            }}
                          >
                            {cellularCheck.uptime}
                          </span>
                        </Typography>
                        <Typography
                          sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                          color='text.secondary'
                        >
                          Location Updated:{' '}
                          <span
                            style={{
                              color: '#154db4',
                              fontFamily: 'BrightstarSans-Regular',
                            }}
                          >
                            {cellularCheck.location_updated}
                          </span>
                        </Typography>
                      </>
                    )}
                    {/* Spinner over left side */}
                    {rebootLoading && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          zIndex: 10,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <div
                          style={{
                            width: '50px',
                            height: '50px',
                            border: '6px solid #ccc',
                            borderTop: '6px solid #1976d2',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                          }}
                        ></div>
                        <p
                          style={{
                            marginTop: '10px',
                            color: '#333',
                            fontWeight: 'bold',
                            fontFamily: 'BrightstarSans-Regular',
                          }}
                        >
                          Rebooting Device...
                        </p>
                        <style>
                          {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
                        </style>
                      </div>
                    )}
                  </div>
                  {/* RIGHT SIDE: Map */}
                  <div
                    style={{
                      width: '50%',
                      marginLeft: '20px',
                      flexShrink: 0,
                    }}
                  >
                    {lat !== null && lng !== null ? (
                      <div className='fade-in'>
                        <MapContainer
                          center={[lat, lng]}
                          zoom={17}
                          style={{ height: '50vh', width: '100%' }}
                        >
                          <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">'
                            url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                          />
                          <Marker position={[lat, lng]} icon={customIcon} />
                        </MapContainer>
                      </div>
                    ) : (
                      <div
                        style={{
                          height: '50vh',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#777',
                          fontSize: '1.2rem',
                        }}
                      >
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='48'
                          height='48'
                          fill='currentColor'
                          viewBox='0 0 16 16'
                          style={{ marginBottom: '10px' }}
                        >
                          <path d='M8 0a5.5 5.5 0 0 0-5.5 5.5c0 3.04 4.9 9.18 5.14 9.49a.5.5 0 0 0 .72 0c.24-.31 5.14-6.45 5.14-9.49A5.5 5.5 0 0 0 8 0zm0 8a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z' />
                        </svg>
                        No map location available
                      </div>
                    )}
                  </div>
                </CardContent>
                {/* Action Buttons Sidepan */}
                <div
                  style={{
                    borderLeft: '1px solid #ccc',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  {/* <Tooltip title='Show Map'>
                    <IconButton onClick={() => setShowMap(!showMap)}>
                      <GpsFixedIcon />
                    </IconButton>
                  </Tooltip> */}
                  <Tooltip title={translations.pingDevice || 'Ping Device'}>
                    <IconButton
                      onClick={() => handlePing()}
                      disabled={rebootLoading}
                    >
                      <SensorsIcon
                        sx={{
                          color: rebootLoading ? 'grey' : 'green',
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title='Cellular Health Check'>
                    <IconButton
                      onClick={() => handleCellularCheck()}
                      disabled={rebootLoading}
                    >
                      <HealthAndSafetyIcon
                        sx={{
                          color: rebootLoading ? 'grey' : 'blue',
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title='Reboot Device'>
                    <IconButton
                      onClick={() => handleReboot()}
                      sx={{ color: rebootLoading ? 'grey' : 'red' }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={7500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Dialog
        open={dialogOpen}
        onClose={handleCancelReboot}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle
          id='alert-dialog-title'
          sx={{ fontFamily: 'BrightstarSans-Regular' }}
        >
          <WarningIcon sx={{ color: 'red' }} />
          {' Warning!'}
        </DialogTitle>
        <DialogContent>
          <Typography
            variant='body2'
            sx={{ fontFamily: 'BrightstarSans-Regular' }}
          >
            Rebooting this device will disrupt service for the customer. Are you
            sure you want to do this?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReboot} color='primary'>
            No
          </Button>
          <Button onClick={handleConfirmReboot} color='primary' autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HughesSearch;
